"use client";

import { useDeviceOrientation } from "@/hooks/use-device-orientation";
import { GAME_THEATER_PLAYER_BACKGROUND } from "@/lib/constants/layout";
import { Z_INDEX_CLASSES } from "@/lib/constants/z-index";
import type { MobileOrientation } from "@/lib/schemas/game";
import { isMobileGameDevice } from "@/lib/utils/device-detection";
import {
  getOrientationPromptMessage,
  shouldShowOrientationPrompt,
} from "@/lib/utils/mobile-orientation";
import { DeviceMobile, X } from "@phosphor-icons/react";
import { useEffect, useState } from "react";

interface GameOrientationPromptProps {
  preference?: MobileOrientation | null;
}

export function GameOrientationPrompt({ preference = "both" }: GameOrientationPromptProps) {
  const deviceOrientation = useDeviceOrientation();
  const [isMobileGameContext, setIsMobileGameContext] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setIsMobileGameContext(isMobileGameDevice());
  }, []);

  const resolvedPreference = preference ?? "both";
  const visible =
    !dismissed &&
    shouldShowOrientationPrompt(resolvedPreference, deviceOrientation, isMobileGameContext);

  if (!visible) {
    return null;
  }

  const message = getOrientationPromptMessage(resolvedPreference);
  const isLandscapeTarget = resolvedPreference === "landscape";
  const rotateClass = isLandscapeTarget
    ? "motion-safe:animate-orientation-rotate-landscape"
    : "motion-safe:animate-orientation-rotate-portrait";

  return (
    <output
      className={`pointer-events-none absolute inset-0 flex items-center justify-center ${Z_INDEX_CLASSES.GAME_PLAYER_LOADING_OVERLAY}`}
      style={{ backgroundColor: GAME_THEATER_PLAYER_BACKGROUND }}
      aria-live="polite"
      data-testid="game-orientation-prompt"
    >
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="pointer-events-auto absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
        aria-label="Dismiss and play in current orientation"
        data-testid="game-orientation-prompt-dismiss"
      >
        <X weight="bold" size={16} />
      </button>
      <div className="flex max-w-xs flex-col items-center gap-4 px-6 text-center">
        <div
          className="flex h-20 w-20 items-center justify-center rounded-2xl bg-white"
          aria-hidden
        >
          <div className={`flex items-center justify-center ${rotateClass}`}>
            <DeviceMobile weight="fill" size={48} className="text-ink" />
          </div>
        </div>
        <p className="text-lg font-semibold text-white">{message}</p>
      </div>
    </output>
  );
}
